/**
 * workspaceAccess.js — dual-workspace routing helpers.
 *
 * Dedicated helpers for the dual-access routing layer (Cert ERP vs
 * HR & Payroll). Deliberately kept SEPARATE from
 * `components/layout/navVisibility.js` — the existing
 * `hasWorkspaceAccess()` / `hasHrPortalAccess()` there feed sidebar/nav
 * visibility across the whole app and must keep their current
 * semantics. These new helpers layer on top of those signals to answer
 * a narrower question: "which of the two operational workspaces
 * (Certification / HR) can this user meaningfully USE right now?".
 *
 * Contract:
 *   • hasCertificationAccess(user)
 *       true when tenant has the "certification" module enabled
 *       AND the user carries at least one Certification-workspace
 *       role or permission (Owner/Admin auto-pass; HR-only users do
 *       NOT auto-pass just because "HR Manager" is in the shared
 *       WORKSPACE_ROLES set — they need a real cert-flavored role or
 *       permission too).
 *
 *   • hasHrAccess(user)
 *       STRICTER than the generic hasWorkspaceAccess. True when:
 *         - tenant has the "hr" module enabled, AND
 *         - the user is Owner OR has role === "HR Manager", OR
 *         - the user has any HR permission (module.hr.* or
 *           hr.self_service).
 *       This is intentionally narrower than "any workspace role" so
 *       generic Sales / Consultant / Finance roles never trigger the
 *       HR workspace even when the tenant is on a bundle plan.
 *
 *   • isDualAccess(user)
 *       true when BOTH of the above are true. Used by HomeRouter,
 *       ChangePlan back-button, WorkspaceSelector tiles, and the
 *       topbar switcher to decide whether the workspace-picker layer
 *       applies at all.
 *
 * No backend changes. Everything read here — roles, permissions,
 * enabled_modules — is already provided by /auth/login and /auth/me.
 */

// Certification-workspace roles. Extracted from the operational nav
// (`NAV_GROUPS` with `module: "certification"`) so that any addition
// to the sidebar has a natural home here too. Deliberately EXCLUDES
// "HR Manager" — that role is HR-first and should only unlock the
// certification workspace when the user also carries a cert-flavored
// permission (see hasCertificationAccess below).
const CERTIFICATION_ROLES = new Set([
  "Owner", "Admin", "Sales Manager", "Sales Executive",
  "Operations Manager", "Consultant", "Compliance Reviewer",
  "Document Controller", "Finance Manager", "Finance User",
  "Renewal Manager", "Trainer", "PRO Executive", "Auditor",
]);

// Certification-workspace permission hints. Same list the sidebar
// gating uses; presence of ANY of these signals a real cert-side scope.
const CERTIFICATION_PERMS = [
  "leads.read", "quotations.read", "cases.read",
  "customers.read", "documents.read", "finance.read",
  "reports.read", "settings.read",
];

function _modulesFor(user) {
  // Canonical shape: `user.enabled_modules` is an array set by the
  // subscription snapshot on login. Legacy users pre-backfill default
  // to BOTH modules so nothing is hidden by surprise.
  if (!user || typeof user !== "object") return [];
  if (Array.isArray(user.enabled_modules)) return user.enabled_modules;
  return ["certification", "hr"];
}

export function hasCertificationAccess(user) {
  if (!user || typeof user !== "object") return false;
  const mods = _modulesFor(user);
  if (!mods.includes("certification")) return false;
  if (user.is_owner) return true;
  const roles = user.roles || [];
  if (roles.some((r) => CERTIFICATION_ROLES.has(r))) return true;
  const perms = user.permissions || [];
  return CERTIFICATION_PERMS.some((k) => perms.includes(k));
}

export function hasHrAccess(user) {
  if (!user || typeof user !== "object") return false;
  const mods = _modulesFor(user);
  if (!mods.includes("hr")) return false;
  if (user.is_owner) return true;
  const roles = user.roles || [];
  if (roles.includes("HR Manager")) return true;
  const perms = user.permissions || [];
  if (perms.includes("hr.self_service")) return true;
  return perms.some((p) => typeof p === "string" && p.startsWith("module.hr."));
}

export function isDualAccess(user) {
  return hasCertificationAccess(user) && hasHrAccess(user);
}

// ─── LocalStorage preference helpers (colocated so callers don't need
//     to import WorkspaceSelector to read/write the key). ───────────
export const WORKSPACE_PREFERENCE_KEY = "certopz.workspace.preference";

export function getPreferredWorkspace() {
  try { return window.localStorage.getItem(WORKSPACE_PREFERENCE_KEY); }
  catch (_err) { return null; }
}

export function setPreferredWorkspace(key) {
  try { window.localStorage.setItem(WORKSPACE_PREFERENCE_KEY, key); }
  catch (_err) { /* noop */ }
}

export function clearPreferredWorkspace() {
  try { window.localStorage.removeItem(WORKSPACE_PREFERENCE_KEY); }
  catch (_err) { /* noop */ }
}
